/**
 * Pure builders that turn a form's draft into the plain-words lines its save preview shows (#1756, FM8). Each says in
 * one or two sentences what pressing the button will do: what will exist, who it affects, and any cost or approval it
 * carries. The wording is the same in Standard and Advanced mode, and a blank part gets a fixed plain fallback.
 *
 * No builder ever outputs a slug, an ID, a raw tool name, JSON or an error code: callers hand in names as the founder
 * knows them (the role's title, the tool's display name, the action as the approvals list names it), never the keys.
 */
export type TaskDraft = {
    /** The role's title, as the founder knows it ("Marketer"). Blank when no role is chosen. */
    roleTitle: string
    goal: string
    /** The actions set to Ask me first, named as the approvals list names them. */
    askFirst: string[]
    /** Spend per run in dollars, or null when there is no cap. */
    spendUsd: number | null
}

export type TeamDraft = {
    name: string
    /** The lead's display name, or null when the COO stands in. */
    leadName: string | null
    /** Concurrent runs held for the team, or null when it draws on the enterprise's free capacity. */
    concurrentRuns: number | null
}

export type MemberDraft = {
    name: string
    teamNames: string[]
    roleTitles: string[]
}

/** "a", "a and b", "a, b and c": a list as a sentence says it. */
export function listInWords(items: string[]): string {
    const named = items.map(item => item.trim()).filter(Boolean)
    if (named.length <= 1) return named[0] ?? ''
    return `${named.slice(0, -1).join(', ')} and ${named[named.length - 1]}`
}

/** Dollars and cents, always: "$0.50", "$2.00". */
function cents(usd: number): string {
    return `$${usd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

/** Whole dollars stay whole ("$50", "$1,000"); anything else shows its cents ("$2.50"). */
function dollars(usd: number): string {
    const whole = Number.isInteger(usd)
    return `$${usd.toLocaleString('en-US', {
        minimumFractionDigits: whole ? 0 : 2,
        maximumFractionDigits: whole ? 0 : 2,
    })}`
}

function isAmount(value: number | null | undefined): value is number {
    return typeof value === 'number' && Number.isFinite(value)
}

/** The goal as the end of "will …": its own words, without a closing full stop that would double the sentence's. */
function goalPhrase(goal: string): string {
    const trimmed = goal.trim().replace(/[.!?]+$/, '')
    return trimmed === '' ? 'work on this task' : trimmed
}

export function buildTaskPreview(draft: TaskDraft): string[] {
    const role = draft.roleTitle.trim()
    const who = role === '' ? 'whoever holds the role' : `your ${role}`
    let sentence = `When this runs, ${who} will ${goalPhrase(draft.goal)}`
    const asks = listInWords(draft.askFirst)
    if (asks !== '') sentence += ` and ask you before ${asks}`
    sentence += '.'
    if (isAmount(draft.spendUsd)) sentence += ` Up to ${cents(draft.spendUsd)} a run.`
    return [sentence]
}

export function buildTeamPreview(draft: TeamDraft): string[] {
    const name = draft.name.trim()
    const team = name === '' ? 'A new team' : `The team ${name}`
    const lead = draft.leadName?.trim()
    const first = lead
        ? `${team} will be created, led by ${lead}.`
        : `${team} will be created, led by the COO until you choose a lead.`
    const second = isAmount(draft.concurrentRuns)
        ? `It can run up to ${draft.concurrentRuns} ${draft.concurrentRuns === 1 ? 'task' : 'tasks'} at the same time.`
        : 'It shares the company’s free capacity for runs.'
    return [first, second]
}

export function buildMemberPreview(draft: MemberDraft): string[] {
    const name = draft.name.trim() || 'A new member'
    const teams = listInWords(draft.teamNames)
    const roles = listInWords(draft.roleTitles)
    let sentence = `${name} will be added`
    if (teams !== '') sentence += ` to ${teams}`
    if (roles !== '') sentence += ` as ${roles}`
    if (teams === '' && roles === '') sentence += ', with no team or role yet'
    return [`${sentence}.`]
}

export type RoleDraft = {
    name: string
    /** The display names of the members who will hold the role. */
    holderNames: string[]
}

export type EnterpriseDraft = {
    name: string
}

export type TeammateDraft = {
    name: string
    /** The job's title ("Customer Support"). */
    jobTitle: string
    teamName: string
    /** The daily spend cap in dollars, or null when the enterprise's cap holds. */
    dailyCapUsd: number | null
    /** The names of the tasks the teammate will take on. */
    taskNames: string[]
}

export type SpendCapDraft = {
    /** What the cap is for, in words ("enterprise", "team", "member", "role", "task"). */
    layerWord: string
    /** The name of the team, member, role or task, or the enterprise's name. Blank when none is chosen. */
    targetName: string
    dayUsd: number | null
    monthUsd: number | null
    runUsd: number | null
    runsPerDay: number | null
    /** The highest model tier the cap allows, as the picker names it ("mid"). Blank or absent says nothing of it. */
    modelTier?: string | null
    /** A cap already exists for this target, so saving replaces it: a limit left blank is then removed. */
    replacesCap?: boolean
}

export type PlanSignInDraft = {
    /** The plan as the founder knows it ("Claude Pro/Max", "ChatGPT plan"). */
    planName: string
    /** Whose sign-in page opens ("Anthropic", "OpenAI"). */
    vendorName: string
}

export type ToolDraft = {
    /** The name the founder gave the tool; blank falls back to a plain phrase. */
    displayName: string
    /** How the tool connects: a token the founder pastes, signing in with OAuth, or a browser it drives. */
    connects: 'token' | 'sign-in' | 'browser'
    /** Where it applies, in words ("the whole company", "a team"). */
    appliesTo: string
}

export type ProviderDraft = {
    /** The provider's name as the founder knows it ("Claude", "OpenAI", "OpenRouter"). */
    providerName: string
}

export function buildRolePreview(draft: RoleDraft): string[] {
    const name = draft.name.trim()
    const role = name === '' ? 'A new role' : `The role ${name}`
    const holders = listInWords(draft.holderNames)
    return holders === ''
        ? [`${role} will be created.`, 'No one holds it yet, so its tasks wait until someone does.']
        : [`${role} will be created, held by ${holders}.`]
}

export function buildEnterprisePreview(draft: EnterpriseDraft): string[] {
    const name = draft.name.trim()
    const company = name === '' ? 'A new company' : name
    return [`${company} will be created, with you as its CEO.`]
}

export function buildTeammatePreview(draft: TeammateDraft): string[] {
    const name = draft.name.trim() || 'A new teammate'
    const job = draft.jobTitle.trim()
    const team = draft.teamName.trim()
    let first = `${name} will join`
    first += team === '' ? ' your company' : ` ${team}`
    if (job !== '') first += ` as ${job}`
    const tasks = listInWords(draft.taskNames)
    if (tasks !== '') first += `, taking on ${tasks}`
    first += '.'
    const second = isAmount(draft.dailyCapUsd)
        ? `They can spend up to ${dollars(draft.dailyCapUsd)} a day.`
        : 'They spend within the company’s own daily cap.'
    return [first, second]
}

export function buildSpendCapPreview(draft: SpendCapDraft): string[] {
    const layer = draft.layerWord.trim()
    const target = draft.targetName.trim()
    let what: string
    if (layer === 'enterprise') what = 'The whole company'
    else if (target === '') what = layer === '' ? 'What you chose' : `The ${layer} you chose`
    else what = layer === '' ? target : `The ${layer} ${target}`

    const limits: string[] = []
    if (isAmount(draft.dayUsd)) limits.push(`${dollars(draft.dayUsd)} a day`)
    if (isAmount(draft.monthUsd)) limits.push(`${dollars(draft.monthUsd)} a month`)
    if (isAmount(draft.runUsd)) limits.push(`${dollars(draft.runUsd)} a run`)
    if (isAmount(draft.runsPerDay)) limits.push(`${draft.runsPerDay} ${draft.runsPerDay === 1 ? 'run' : 'runs'} a day`)

    const tier = draft.modelTier?.trim() ?? ''
    const models = tier === '' ? '' : `, on models up to the ${tier} tier`
    const first =
        limits.length === 0
            ? `${what} will get a cap with no limit set${models}.`
            : `${what} will be capped at ${listInWords(limits)}${models}.`
    if (!draft.replacesCap) return [first]
    return [
        first,
        limits.length === 0
            ? 'This replaces its current cap, so every limit it has now is removed.'
            : 'This replaces its current cap, so any limit left blank is removed.',
    ]
}

/** A plan sign-in on Add AI provider: what is connected once the founder signs in, and what the runs then bill. */
export function buildPlanSignInPreview(draft: PlanSignInDraft): string[] {
    const plan = draft.planName.trim() || 'plan'
    const vendor = draft.vendorName.trim()
    const first =
        vendor === ''
            ? `Your ${plan} will be connected as an AI provider once you sign in.`
            : `Your ${plan} will be connected as an AI provider once you sign in with ${vendor}.`
    return [first, 'Runs then bill that subscription seat.']
}

export function buildToolPreview(draft: ToolDraft): string[] {
    const name = draft.displayName.trim()
    const tool = name === '' ? 'A new tool' : name
    const where = draft.appliesTo.trim() || 'the place you chose'
    if (draft.connects === 'sign-in') return [`${tool} will be connected for ${where} once you sign in to it.`]
    if (draft.connects === 'browser') return [`${tool} will get a browser for ${where}, kept to the sites you listed.`]
    return [`${tool} will be connected for ${where} with the key you gave.`]
}

export function buildProviderPreview(draft: ProviderDraft): string[] {
    const name = draft.providerName.trim()
    return name === ''
        ? ['A new AI provider will be connected, and your runs can use its models.']
        : [`${name} will be connected as an AI provider, and your runs can use its models.`]
}
